/**
 * ComponentsModal — Install/manage physical components (NICs, HBAs, etc.)
 * inside a rack element (server). Each component has typed ports.
 */

import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import './ComponentsModal.css';

const COMP_TYPES  = ['cpu', 'ram', 'nic', 'hba', 'gpu', 'raid', 'psu', 'bmc', 'other'];
const COMP_ICONS  = { cpu: '🧠', ram: '📦', nic: '🌐', hba: '💾', gpu: '🎮', raid: '🗄️', psu: '⚡', bmc: '🔧', other: '🔧' };
const COMP_LABELS = { cpu: 'Procesador (CPU)', ram: 'Memoria RAM', nic: 'Tarjeta de red (NIC)', hba: 'HBA (Fibre Channel)', gpu: 'GPU', raid: 'Controladora RAID', psu: 'Fuente de alimentación', bmc: 'BMC / iLO (gestión)', other: 'Otro' };
const PORT_TYPES  = ['rj45', 'sfp', 'sfp+', 'qsfp', 'qsfp28', 'fc'];
const PORT_SPEEDS = ['', '100M', '1G', '2.5G', '10G', '25G', '40G', '100G'];
const NETCFG_PREFIX = 'NETCFG:';

function parseNetCfg(notes) {
  if (!notes) return {};
  const line = String(notes)
    .split('\n')
    .map(l => l.trim())
    .find(l => l.startsWith(NETCFG_PREFIX));
  if (!line) return {};
  try {
    return JSON.parse(line.slice(NETCFG_PREFIX.length).trim()) || {};
  } catch {
    return {};
  }
}

function stripNetCfg(notes) {
  return String(notes || '')
    .split('\n')
    .filter(l => !String(l).trim().startsWith(NETCFG_PREFIX))
    .join('\n')
    .trim();
}

function mergeNetCfg(notes, cfg) {
  const cleanNotes = stripNetCfg(notes);
  const hasCfg = cfg && Object.values(cfg).some(v => v !== '' && v !== null && v !== false);
  if (!hasCfg) return cleanNotes;
  const serialized = `${NETCFG_PREFIX} ${JSON.stringify(cfg)}`;
  return cleanNotes ? `${cleanNotes}\n${serialized}` : serialized;
}

function ComponentsModal({ element, companyId, onClose }) {
  const [components,  setComponents]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [editComp,    setEditComp]    = useState(null);   // null=hidden, {}=new, {...}=edit
  const [editPort,    setEditPort]    = useState(null);   // { compId, port: {}|{...} }
  const [deleteComp,  setDeleteComp]  = useState(null);
  const [deletePort,  setDeletePort]  = useState(null);  // { compId, port }
  const [saving,      setSaving]      = useState(false);

  const fetchComponents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getComponents(element.id, companyId);
      setComponents(res.data || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [element.id, companyId]);

  useEffect(() => { fetchComponents(); }, [fetchComponents]);

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  // ── Component CRUD ───────────────────────────────────────────────────────

  const handleSaveComp = async (formData) => {
    setSaving(true);
    setError('');
    try {
      if (editComp?.id) await api.updateComponent(element.id, editComp.id, formData, companyId);
      else              await api.createComponent(element.id, formData, companyId);
      setEditComp(null);
      fetchComponents();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDeleteComp = async () => {
    setSaving(true);
    try {
      await api.deleteComponent(element.id, deleteComp.id, companyId);
      setDeleteComp(null);
      fetchComponents();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ── Port CRUD ────────────────────────────────────────────────────────────

  const handleSavePort = async (compId, portId, formData) => {
    setSaving(true);
    setError('');
    try {
      if (portId) await api.updateComponentPort(element.id, compId, portId, formData, companyId);
      else        await api.createComponentPort(element.id, compId, formData, companyId);
      setEditPort(null);
      fetchComponents();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDeletePort = async () => {
    setSaving(true);
    try {
      await api.deleteComponentPort(element.id, deletePort.compId, deletePort.port.id, companyId);
      setDeletePort(null);
      fetchComponents();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="cmp-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cmp-modal">

        {/* Header */}
        <div className="cmp-header">
          <span className="cmp-header-icon">🧩</span>
          <div>
            <h3>Componentes de rack</h3>
            <p>{element.name}</p>
          </div>
          <button className="cmp-close" onClick={onClose}>✕</button>
        </div>

        <div className="cmp-body">
          {error && <div className="alert alert-danger mb-3">{error}</div>}

          <div className="cmp-top-actions">
            <button className="cmp-btn-primary" onClick={() => setEditComp({})}>
              + Añadir componente
            </button>
          </div>

          {loading ? (
            <div className="cmp-loading"><div className="spinner-border" /><span>Cargando...</span></div>
          ) : components.length === 0 ? (
            <div className="cmp-empty">
              <span>🧩</span>
              <p>Sin componentes instalados</p>
              <button className="cmp-btn-primary" onClick={() => setEditComp({})}>+ Añadir componente</button>
            </div>
          ) : (
            components.map(comp => (
              <div key={comp.id} className="cmp-card">
                <div className="cmp-card-header">
                  <span className="cmp-card-icon">{COMP_ICONS[comp.component_type] || '🔧'}</span>
                  <div className="cmp-card-info">
                    <strong>{comp.manufacturer || ''} {comp.model || <em>Sin modelo</em>}</strong>
                    <span>{COMP_LABELS[comp.component_type] || comp.component_type}{comp.slot ? ` · ${comp.slot}` : ''}</span>
                  </div>
                  <span className={`cmp-status cmp-status--${comp.status}`}>{comp.status}</span>
                  <div className="cmp-card-actions">
                    <button className="cmp-icon-btn" title="Añadir puerto" onClick={() => setEditPort({ compId: comp.id, port: {} })}>+ Puerto</button>
                    <button className="cmp-icon-btn" title="Editar" onClick={() => setEditComp(comp)}>✏️</button>
                    <button className="cmp-icon-btn cmp-icon-btn--del" title="Eliminar" onClick={() => setDeleteComp(comp)}>🗑️</button>
                  </div>
                </div>

                {/* Port table */}
                {comp.ports && comp.ports.length > 0 ? (
                  <table className="cmp-ports-table">
                    <thead>
                      <tr><th>#</th><th>Label</th><th>Tipo</th><th>Velocidad</th><th>MAC / WWN</th><th>Conexión activa</th><th>Config NIC</th><th></th></tr>
                    </thead>
                    <tbody>
                      {comp.ports.map(port => (
                        <tr key={port.id}>
                          <td><strong>{port.port_number}</strong></td>
                          <td>{port.label || '—'}</td>
                          <td><span className="cmp-port-type">{port.port_type}</span></td>
                          <td>{port.speed || '—'}</td>
                          <td><code className="cmp-mac">{port.mac_address || port.wwn || '—'}</code></td>
                          <td>
                            {port.connection ? (
                              <span className="cmp-port-connected">
                                ● {port.connection.other_end?.element_name || ''}
                                {port.connection.other_end?.port_number != null && <> P{port.connection.other_end.port_number}</>}
                                {port.connection.cable_type && <span className="cmp-cable"> [{port.connection.cable_type}]</span>}
                              </span>
                            ) : <span className="cmp-port-free">○ Libre</span>}
                          </td>
                          <td>
                            {(() => {
                              const ncfg = parseNetCfg(port.notes);
                              const tags = [];
                              if (ncfg.team_name) tags.push(`Team ${ncfg.team_name}${ncfg.team_mode ? ` (${ncfg.team_mode})` : ''}`);
                              if (ncfg.vlan_id) tags.push(`VLAN ${ncfg.vlan_id}`);
                              if (ncfg.mtu) tags.push(`MTU ${ncfg.mtu}`);
                              if (ncfg.ip_mode === 'dhcp') tags.push('DHCP');
                              if (ncfg.ip_mode === 'static' && ncfg.ip_address) tags.push(`IP ${ncfg.ip_address}`);
                              return tags.length > 0 ? (
                                <div style={{ marginTop: 3, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                  {tags.map((tag, idx) => (
                                    <span key={idx} style={{ fontSize: '0.66rem', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 4, padding: '1px 5px' }}>
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              ) : null;
                            })()}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button className="cmp-icon-btn" onClick={() => setEditPort({ compId: comp.id, port })}>✏️</button>
                              <button className="cmp-icon-btn cmp-icon-btn--del" onClick={() => setDeletePort({ compId: comp.id, port })}>🗑️</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="cmp-no-ports">
                    Sin puertos.{' '}
                    <button className="cmp-link" onClick={() => setEditPort({ compId: comp.id, port: {} })}>Añadir puerto</button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Component form */}
        {editComp !== null && (
          <ComponentForm comp={editComp} onSave={handleSaveComp} onCancel={() => setEditComp(null)} saving={saving} />
        )}

        {/* Port form */}
        {editPort !== null && (
          <PortForm editPort={editPort} onSave={handleSavePort} onCancel={() => setEditPort(null)} saving={saving} />
        )}

        {/* Delete component confirm */}
        {deleteComp && (
          <div className="cmp-confirm-overlay">
            <div className="cmp-confirm">
              <h4>¿Eliminar componente?</h4>
              <p>Se eliminará <strong>{deleteComp.manufacturer} {deleteComp.model}</strong> y sus {deleteComp.ports?.length || 0} puertos (y sus conexiones activas).</p>
              <div className="cmp-confirm-actions">
                <button className="cmp-btn-secondary" onClick={() => setDeleteComp(null)} disabled={saving}>Cancelar</button>
                <button className="cmp-btn-danger" onClick={handleDeleteComp} disabled={saving}>{saving ? '...' : 'Eliminar'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete port confirm */}
        {deletePort && (
          <div className="cmp-confirm-overlay">
            <div className="cmp-confirm">
              <h4>¿Eliminar puerto?</h4>
              <p>Puerto <strong>{deletePort.port.port_number}</strong>{deletePort.port.connection ? ' (tiene conexión activa que también se eliminará)' : ''}.</p>
              <div className="cmp-confirm-actions">
                <button className="cmp-btn-secondary" onClick={() => setDeletePort(null)} disabled={saving}>Cancelar</button>
                <button className="cmp-btn-danger" onClick={handleDeletePort} disabled={saving}>{saving ? '...' : 'Eliminar'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Component form ───────────────────────────────────────────────────────────

function ComponentForm({ comp, onSave, onCancel, saving }) {
  const isEdit = Boolean(comp?.id);
  const [form, setForm] = useState({
    component_type: comp?.component_type || 'nic',
    slot:           comp?.slot           || '',
    manufacturer:   comp?.manufacturer   || '',
    model:          comp?.model          || '',
    serial_number:  comp?.serial_number  || '',
    asset_tag:      comp?.asset_tag      || '',
    status:         comp?.status         || 'active',
    notes:          comp?.notes          || '',
  });
  const [err, setErr] = useState('');

  const handleSubmit = e => {
    e.preventDefault();
    setErr('');
    if (!form.component_type) { setErr('Tipo obligatorio.'); return; }
    onSave(form);
  };

  return (
    <div className="cmp-form-overlay" onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="cmp-form-modal">
        <div className="cmp-form-header">
          <h4>{isEdit ? 'Editar componente' : 'Nuevo componente'}</h4>
          <button className="cmp-close" onClick={onCancel}>✕</button>
        </div>
        <form className="cmp-form-body" onSubmit={handleSubmit}>
          {err && <div className="alert alert-danger">{err}</div>}
          <div className="cmp-form-row">
            <div className="cmp-form-field">
              <label>Tipo *</label>
              <select className="cmp-input" value={form.component_type} onChange={e => setForm(f => ({ ...f, component_type: e.target.value }))}>
                {COMP_TYPES.map(t => <option key={t} value={t}>{COMP_LABELS[t]}</option>)}
              </select>
            </div>
            <div className="cmp-form-field">
              <label>Slot / Posición</label>
              <input className="cmp-input" placeholder="PCIe-2, OCP-1..." value={form.slot} onChange={e => setForm(f => ({ ...f, slot: e.target.value }))} />
            </div>
          </div>
          <div className="cmp-form-row">
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Fabricante</label>
              <input className="cmp-input" placeholder="Intel, Broadcom..." value={form.manufacturer} onChange={e => setForm(f => ({ ...f, manufacturer: e.target.value }))} />
            </div>
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Modelo</label>
              <input className="cmp-input" placeholder="X710-DA2, HBA 16Gb..." value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} />
            </div>
          </div>
          <div className="cmp-form-row">
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Nº de serie</label>
              <input className="cmp-input" placeholder="S/N" value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} />
            </div>
            <div className="cmp-form-field" style={{ flex: '0 0 130px' }}>
              <label>Estado</label>
              <select className="cmp-input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="active">Activo</option><option value="faulty">Averiado</option><option value="spare">Repuesto</option>
              </select>
            </div>
          </div>
          <div className="cmp-form-field">
            <label>Notas</label>
            <textarea className="cmp-input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="cmp-form-actions">
            <button type="button" className="cmp-btn-secondary" onClick={onCancel} disabled={saving}>Cancelar</button>
            <button type="submit" className="cmp-btn-primary" disabled={saving}>{saving ? '⏳...' : isEdit ? '💾 Guardar' : '+ Añadir'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Port form ────────────────────────────────────────────────────────────────

function PortForm({ editPort, onSave, onCancel, saving }) {
  const { compId, port } = editPort;
  const isEdit = Boolean(port?.id);
  const existingCfg = parseNetCfg(port?.notes);
  const [form, setForm] = useState({
    port_number:  port?.port_number  || '',
    label:        port?.label        || '',
    port_type:    port?.port_type    || 'rj45',
    speed:        port?.speed        || '',
    mac_address:  port?.mac_address  || '',
    wwn:          port?.wwn          || '',
    notes:        port?.notes        || '',
    is_active:    port?.is_active    ?? true,
    team_name:    existingCfg.team_name    || '',
    team_mode:    existingCfg.team_mode    || '',
    team_role:    existingCfg.team_role    || '',
    vlan_id:      existingCfg.vlan_id      || '',
    mtu:          existingCfg.mtu          || '',
    autoneg:      existingCfg.autoneg      ?? true,
    duplex:       existingCfg.duplex       || '',
    ip_mode:      existingCfg.ip_mode      || '',
    ip_address:   existingCfg.ip_address   || '',
    prefix:       existingCfg.prefix       || '',
    gateway:      existingCfg.gateway      || '',
    dns:          existingCfg.dns          || '',
  });
  const [err, setErr] = useState('');

  const handleSubmit = e => {
    e.preventDefault();
    setErr('');
    const netCfg = {
      team_name: form.team_name,
      team_mode: form.team_mode,
      team_role: form.team_role,
      vlan_id: form.vlan_id,
      mtu: form.mtu,
      autoneg: form.autoneg,
      duplex: form.duplex,
      ip_mode: form.ip_mode,
      ip_address: form.ip_address,
      prefix: form.prefix,
      gateway: form.gateway,
      dns: form.dns,
    };

    const notesWithCfg = mergeNetCfg(form.notes, netCfg);

    onSave(compId, port?.id || null, {
      ...form,
      notes: notesWithCfg,
      port_number: form.port_number ? Number(form.port_number) : undefined,
    });
  };

  return (
    <div className="cmp-form-overlay" onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="cmp-form-modal">
        <div className="cmp-form-header">
          <h4>{isEdit ? 'Editar puerto' : 'Nuevo puerto'}</h4>
          <button className="cmp-close" onClick={onCancel}>✕</button>
        </div>
        <form className="cmp-form-body" onSubmit={handleSubmit}>
          {err && <div className="alert alert-danger">{err}</div>}
          <div className="cmp-form-row">
            <div className="cmp-form-field" style={{ flex: '0 0 100px' }}>
              <label>Número</label>
              <input className="cmp-input" type="number" min="0" placeholder="0" value={form.port_number} onChange={e => setForm(f => ({ ...f, port_number: e.target.value }))} disabled={isEdit} />
            </div>
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Label</label>
              <input className="cmp-input" placeholder="eth0, hba0..." value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
            </div>
            <div className="cmp-form-field">
              <label>Tipo</label>
              <select className="cmp-input" value={form.port_type} onChange={e => setForm(f => ({ ...f, port_type: e.target.value }))}>
                {PORT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="cmp-form-field">
              <label>Velocidad</label>
              <select className="cmp-input" value={form.speed} onChange={e => setForm(f => ({ ...f, speed: e.target.value }))}>
                {PORT_SPEEDS.map(s => <option key={s} value={s}>{s || '—'}</option>)}
              </select>
            </div>
          </div>
          <div className="cmp-form-row">
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Dirección MAC</label>
              <input className="cmp-input" placeholder="00:11:22:33:44:55" value={form.mac_address} onChange={e => setForm(f => ({ ...f, mac_address: e.target.value }))} />
            </div>
            <div className="cmp-form-field cmp-form-field--grow">
              <label>WWN (FC)</label>
              <input className="cmp-input" placeholder="50:01:43:80:14:00:1b:7d" value={form.wwn} onChange={e => setForm(f => ({ ...f, wwn: e.target.value }))} />
            </div>
          </div>

          <div className="cmp-form-row">
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Team / Bond</label>
              <input className="cmp-input" placeholder="bond0 / team0" value={form.team_name} onChange={e => setForm(f => ({ ...f, team_name: e.target.value }))} />
            </div>
            <div className="cmp-form-field">
              <label>Modo teaming</label>
              <select className="cmp-input" value={form.team_mode} onChange={e => setForm(f => ({ ...f, team_mode: e.target.value }))}>
                <option value="">—</option>
                <option value="active-backup">active-backup</option>
                <option value="lacp">lacp (802.3ad)</option>
                <option value="switch-independent">switch-independent</option>
                <option value="static">static</option>
              </select>
            </div>
            <div className="cmp-form-field">
              <label>Rol team</label>
              <select className="cmp-input" value={form.team_role} onChange={e => setForm(f => ({ ...f, team_role: e.target.value }))}>
                <option value="">—</option>
                <option value="primary">primary</option>
                <option value="member">member</option>
                <option value="standby">standby</option>
              </select>
            </div>
          </div>

          <div className="cmp-form-row">
            <div className="cmp-form-field">
              <label>VLAN ID</label>
              <input className="cmp-input" type="number" min="1" max="4094" placeholder="10" value={form.vlan_id} onChange={e => setForm(f => ({ ...f, vlan_id: e.target.value }))} />
            </div>
            <div className="cmp-form-field">
              <label>MTU</label>
              <input className="cmp-input" type="number" min="576" max="9216" placeholder="1500" value={form.mtu} onChange={e => setForm(f => ({ ...f, mtu: e.target.value }))} />
            </div>
            <div className="cmp-form-field">
              <label>Dúplex</label>
              <select className="cmp-input" value={form.duplex} onChange={e => setForm(f => ({ ...f, duplex: e.target.value }))}>
                <option value="">—</option>
                <option value="auto">auto</option>
                <option value="full">full</option>
                <option value="half">half</option>
              </select>
            </div>
            <div className="cmp-form-field" style={{ alignSelf: 'end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
                <input type="checkbox" checked={form.autoneg} onChange={e => setForm(f => ({ ...f, autoneg: e.target.checked }))} />
                Autoneg
              </label>
            </div>
          </div>

          <div className="cmp-form-row">
            <div className="cmp-form-field">
              <label>IP mode</label>
              <select className="cmp-input" value={form.ip_mode} onChange={e => setForm(f => ({ ...f, ip_mode: e.target.value }))}>
                <option value="">—</option>
                <option value="dhcp">dhcp</option>
                <option value="static">static</option>
                <option value="none">none</option>
              </select>
            </div>
            <div className="cmp-form-field cmp-form-field--grow">
              <label>IP address</label>
              <input className="cmp-input" placeholder="192.168.1.10" value={form.ip_address} onChange={e => setForm(f => ({ ...f, ip_address: e.target.value }))} />
            </div>
            <div className="cmp-form-field">
              <label>Prefix</label>
              <input className="cmp-input" type="number" min="1" max="32" placeholder="24" value={form.prefix} onChange={e => setForm(f => ({ ...f, prefix: e.target.value }))} />
            </div>
          </div>

          <div className="cmp-form-row">
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Gateway</label>
              <input className="cmp-input" placeholder="192.168.1.1" value={form.gateway} onChange={e => setForm(f => ({ ...f, gateway: e.target.value }))} />
            </div>
            <div className="cmp-form-field cmp-form-field--grow">
              <label>DNS</label>
              <input className="cmp-input" placeholder="8.8.8.8, 1.1.1.1" value={form.dns} onChange={e => setForm(f => ({ ...f, dns: e.target.value }))} />
            </div>
          </div>
          <div className="cmp-form-actions">
            <button type="button" className="cmp-btn-secondary" onClick={onCancel} disabled={saving}>Cancelar</button>
            <button type="submit" className="cmp-btn-primary" disabled={saving}>{saving ? '⏳...' : isEdit ? '💾 Guardar' : '+ Añadir'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ComponentsModal;
